/**
 * Password field embedded in the landing hero. On success, unlocks the auth
 * store and navigates the user to /app. The component is intentionally lean
 * — visuals live in the Landing route.
 */

import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Lock, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { checkPassword, useAuthStore } from "@/lib/auth";
import { cn } from "@/lib/utils";

export function PasswordGate({ className }: { className?: string }) {
  const navigate = useNavigate();
  const unlock = useAuthStore((s) => s.unlock);

  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!value) return;
    setBusy(true);
    setError(null);
    try {
      const ok = await checkPassword(value);
      if (!ok) {
        setError("That password isn't right.");
        setBusy(false);
        return;
      }
      unlock();
      navigate("/app");
    } catch {
      setError("Something went wrong. Try again.");
      setBusy(false);
    }
  }

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
      className={cn("flex flex-col gap-3 w-full max-w-md", className)}
    >
      <label className="flex items-center gap-2 text-xs font-semibold text-ink-400 uppercase tracking-wider">
        <Lock className="size-3.5" />
        Members preview · Password protected
      </label>

      <div className="relative">
        <Input
          type={show ? "text" : "password"}
          autoComplete="current-password"
          value={value}
          invalid={!!error}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError(null);
          }}
          placeholder="Enter access password"
          className="pr-12 h-14 text-base"
        />
        <button
          type="button"
          aria-label={show ? "Hide password" : "Show password"}
          onClick={() => setShow((v) => !v)}
          className="absolute inset-y-0 right-3 flex items-center text-ink-400 hover:text-ink-200 transition-colors"
        >
          {show ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
        </button>
      </div>

      <Button
        type="submit"
        size="lg"
        disabled={!value}
        loading={busy}
        iconRight={<ArrowRight className="size-4" />}
      >
        Continue to preview
      </Button>

      {error && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-danger"
        >
          {error}
        </motion.p>
      )}

      <p className="text-xs text-ink-500 mt-1">
        The full IntraAlpha is launching soon. The preview behind this gate is
        for early supporters — content is illustrative.
      </p>
    </motion.form>
  );
}
